import {AfterViewInit, Component, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {SmsVerificationComponent} from '../../public/sms-verification/sms-verification.component';
import {Channel} from '../../public/signup/signup.component';
import {AccountVerificationService} from '../../account-verification.service';
import {getUserId, mobile} from '../../consts/utilities';
import {MatDialog} from '@angular/material/dialog';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {UserIntegrityService} from '../../user-integrity.service';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {AppTitleService} from '../../app-title.service';
import {ProfileDataService} from '../../profile/profile-data.service';
import {MCEmail} from '../../profile/models/mc.email.model';
import {MCPhone} from '../../profile/models/mc.phone.model';
import {ValidatorsService} from '../../services/validators.service';

@Component({
    selector: 'app-contact-step',
    templateUrl: './contact-step.component.html',
    styleUrls: ['./contact-step.component.scss']
})
export class ContactStepComponent implements OnInit, AfterViewInit {
    public onContactRequiredScreen = true;
    public onValidationAccount = false;

    public userHaveEmails = false;
    public userHavePhones = false;

    public userNeedNewContact = false;

    public emails: MCEmail[] = [];
    public phones: MCPhone[] = [];
    public config: any;

    @ViewChild('contactVerifier', { static: true })
    contactVerifier: SmsVerificationComponent;

    @ViewChild('unableToDeliverCode', { static: true })
    unableToSendCode: TemplateRef<any>;

    @Input() allowEmails = false;
    @Input() allowPhones = false;

    public contactForm = new FormGroup({
        country: new FormControl(52, [Validators.minLength(1), Validators.maxLength(3)]),
        account: new FormControl('', [Validators.required]),
    }, { asyncValidators: this.validatorService.profileAccountValidation });

    // @ts-ignore
    public email: MCEmail = new MCEmail();
    public phone: MCPhone = new MCPhone();

    constructor(
        private titleService: AppTitleService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private dialog: MatDialog,
        private bottomSheet: MatBottomSheet,
        private profileDataService: ProfileDataService,
        private validatorService: ValidatorsService,
        private accountVerificationService: AccountVerificationService,
        private userIntegrityService: UserIntegrityService,
    ) {}

    ngOnInit(): void {
        this.activatedRoute.queryParams.subscribe(data => {
            const { type } = data;

            if (!type) {
                this.allowEmails = true;
                this.allowPhones = true;
            }

            if (type === 'phone') {
                this.allowEmails = false;
                this.allowPhones = true;
            }

            if (type === 'email') {
                this.allowPhones = false;
                this.allowEmails = true;
            }
        });

        this.activatedRoute.data.subscribe(data => {
            const { response } = data;
            const {
                phoneResponse,
                phoneConfigResponse,
                emailResponse,
                emailConfigResponse,
            } = response;

            if (this.allowPhones) {
                if (phoneResponse instanceof HttpErrorResponse) {
                    this.userHavePhones = false;
                    this.userNeedNewContact = true;
                    return;
                }

                const { data: phones } = phoneResponse;
                const { data: phoneConfig } = phoneConfigResponse;

                this.phones = phones;
                this.userHavePhones = phones.length > 0;
                this.config = phoneConfig[0];

                this.userNeedNewContact = !this.userHavePhones;
            }

            if (this.allowEmails) {
                if (emailResponse instanceof HttpErrorResponse) {
                    this.userHaveEmails = false;
                    this.userNeedNewContact = true;
                    return;
                }

                const { data: emails } = emailResponse;
                const { data: emailConfig } = emailConfigResponse;

                this.emails = emails;
                this.userHaveEmails = emails.length > 0;
                this.config = emailConfig[0];

                this.userNeedNewContact = !this.userHaveEmails;
            }
        });
    }

    ngAfterViewInit() {
        if (this.allowEmails && !this.allowPhones) {
            this.titleService.setTitle('Correo electronico');
        }

        if (this.allowPhones && !this.allowEmails) {
            this.titleService.setTitle('Num. de telefono');
        }
    }

    public selectedEmail(email: MCEmail): void {
        this.email = email;
    }

    public selectedPhone(phone: MCPhone): void {
        this.phone = phone;
    }

    public sendCodeForValidation(): void {
        if (this.allowEmails && !this.allowPhones) {
            this.onNewEmailAdded();
        }

        if (this.allowPhones && !this.allowEmails) {
            this.onNewPhoneAdded();
        }
    }

    public onUserRequireAnotherData(): void {
        this.userNeedNewContact = true;

        this.phone = new MCPhone();
        // @ts-ignore
        this.email = new MCEmail();
    }

    public onUserWantSelectData(): void {
        this.userNeedNewContact = false;
    }

    public skipData(type: string = '?'): void {
        if (type === '?') {
            type = this.allowEmails && !this.allowPhones ? 'email' : 'phone';
        }

        if (type === 'email') {
            sessionStorage.setItem('user-skip-email', '1');
        } else {
            sessionStorage.setItem('user-skip-phone', '1');
        }

        this.onAddedResponse();
    }

    private onNewEmailAdded(): void {
        if (this.userNeedNewContact) {
            const account = this.contactForm.get('account').value;
            this.email.email = account;
        }

        this.openCodeValidationComp(this.email.email, Channel.Email);
    }

    private onNewPhoneAdded(): void {
        if (this.userNeedNewContact) {
            const countryValue = this.contactForm.get('country').value;
            const phoneValue = this.contactForm.get('account').value;

            this.phone.pais = countryValue;
            this.phone.telefono = phoneValue;
        }

        this.openCodeValidationComp(`${this.phone.pais}${this.phone.telefono}`, Channel.SMS);
    }

    public openCodeValidationComp(account: string, channel: Channel): void {
        const id = getUserId();

        this.accountVerificationService.sendLinkCode(id, account).subscribe(
            (res) => {
                // Open verification assistant
                this.onContactRequiredScreen = false;
                this.onValidationAccount = true;

                this.contactVerifier.open({
                    id: +id,
                    channel,
                    typeOfVerification: 'non-signup',
                    account,
                    state: 'sendingCode',
                    isForReset: false,
                });
            },
            (error: HttpErrorResponse) => {
                // show unable to verify modal
                if (mobile()) {
                    this.bottomSheet.open(this.unableToSendCode);
                }

                if (!mobile()) {
                    this.dialog.open(this.unableToSendCode);
                }
            },
        );
    }

    public onUserVerifiedContact(event: any): void {
        if (this.allowPhones && !this.allowEmails) {
            this.phone.sw_valida = 1;
            const json = JSON.stringify(this.phone);
            this.profileDataService.phoneRequest('IN', json, (result: any) => {
                const { statusCode, data } = result;

                if (statusCode !== HttpStatusCode.Ok) {
                    return;
                }

                this.onAddedResponse();
            });
        }

        if (this.allowEmails && !this.allowPhones) {
            this.email.sw_valida = 1;
            const json = JSON.stringify(this.email);
            this.profileDataService.emailRequest('IN', json, (result: any) => {
                const { statusCode, data } = result;

                if (statusCode !== HttpStatusCode.Ok) {
                    return;
                }

                this.onAddedResponse();
            });
        }
    }

    private onAddedResponse(): void {
        // next step logic
        this.userIntegrityService.checkNow();

        const checkerValue = this.userIntegrityService.currentValue;
        const { card, email, zip, phone } = checkerValue;

        if (this.allowPhones) {
            if (card === 1 && zip === 1 && email === 1) {
                this.router.navigate(['/app/dashboard/cards']);
                return;
            }
        }

        if (this.allowEmails) {
            if (card === 1 && zip === 1 && phone === 1) {
                this.router.navigate(['/app/dashboard/cards']);
                return;
            }
        }
    }

    public dismissMessage(): void {
        if (mobile()) {
            this.bottomSheet.dismiss();
        }

        if (!mobile()) {
            this.dialog.closeAll();
        }
    }
}
